/* eslint-disable react/jsx-props-no-spreading */
import { Button, Center, Loader, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { closeAllModals, openModal } from '@mantine/modals';
import { FC, useMemo, useState } from 'react';
import BookitupSelect from '../../../../Atoms/BookitupSelect';
import { DataS } from '../../../../Service/DataS';
import { PositionS } from '../../../../Service/PositionS';
import { ResourceType, ResourceUsage } from '../../../../Types/ResourceT';
import CategoryInputField from '../../../Resources/CategoryInputField';
import AutocompleteResourceTypeItem from '../../../Resources/table/AutocompleteResourceTypeItem';
import { BrowserS } from '../../../../Service/BrowserS';
import { EventRichInput } from '../../../tiptap/TiptapEditorInput';
import { TiptapS } from '../../../../Service/TiptapS';
import { parsePlaceholders } from '../../../../Service/Placeholder/PlaceholderResover';
import type { ResourcePosition } from './ResourcePositionAssignment';

interface Props {
  favoritePositionId: number;
  initialName: string;
  initialDescription: string;
  setResourcePosition: (resourcePosition: ResourcePosition) => void;
}

export const openResourcePositionCreateFromFavoriteModal = (props: Props) =>
  openModal({
    size: 'sm',
    title: (
      <Text weight="bolder" size="xl">
        Ressource erstellen
      </Text>
    ),
    closeOnEscape: true,
    transition: 'slide-down',
    children: <ResourcePositionCreateFromFavoriteModal {...props} />,
  });

const ResourcePositionCreateFromFavoriteModal: FC<Props> = ({
  favoritePositionId,
  initialName,
  initialDescription,
  setResourcePosition,
}) => {
  const [loading, setLoading] = useState(false);
  const form = useForm({
    initialValues: {
      name: BrowserS.getTextContent(initialName),
      description: initialDescription,
      type: ResourceType.ITEM,
      categoryName: null as string | null,
      usage: ResourceUsage.RENTAL,
    },
    validate: {
      name: (value) => (value && value.trim().length > 0 ? null : 'Bitte geben Sie die Bezeichnung ein'),
    },
  });

  const { description } = form.values;

  const containsPlaceholders = useMemo(() => parsePlaceholders(description).length > 0, [description]);

  const onSubmit = async () => {
    setLoading(true);
    const created = await PositionS.createResourceFromFavorite(favoritePositionId, {
      ...form.values,
    });
    setLoading(false);

    if (created) {
      setResourcePosition(created);
      closeAllModals();
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack spacing="xs">
        <TextInput label="Name" {...form.getInputProps('name')} />
        <EventRichInput
          label="Beschreibung"
          value={form.values.description}
          onUpdate={(html) => form.setFieldValue('description', html)}
          styles={TiptapS.TextInputStyles}
        />
        <BookitupSelect
          mt="md"
          label="Typ"
          sx={{ marginTop: '0px !important' }}
          variant="default"
          data={DataS.resourceTypeOptions}
          itemComponent={AutocompleteResourceTypeItem}
          {...form.getInputProps('type')}
        />
        <CategoryInputField
          initialName={form.values.categoryName ?? undefined}
          onSelected={(category) => form.setFieldValue('categoryName', category)}
        />
        <Center mb="md" mt="xl">
          <Button
            type="submit"
            disabled={loading || !form.isValid() || containsPlaceholders}
            leftIcon={loading && <Loader size="xs" />}
            sx={{ width: 240 }}
          >
            Speichern
          </Button>
        </Center>
      </Stack>
    </form>
  );
};

export default ResourcePositionCreateFromFavoriteModal;
